/**
 * Letter-first onboarding — Orion's interview.
 *
 * Three questions, every one skippable, then a draft or an outline built
 * from the answers. Answers persist per user so a second letter to a
 * different recipient starts from what they already said — stored answers
 * pre-fill here, and the user can edit them per recipient.
 */
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import React, { useEffect, useState } from 'react';
import {
  View,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  Image,
  TextInput,
  ActivityIndicator,
  Alert,
  ScrollView,
} from 'react-native';

import { AppText as Text } from '../../components/AppText';
import OrnamentBackground from '../../components/OrnamentBackground';
import { API_URL } from '../../config';
import { getAuthToken } from '../../services/auth/tokenStorage';
import { letterToLovedOnesService } from '../../services/letterToLovedOnesInfo';
import { createLogger } from '../../utils/logger';
import { ms, s } from '../../utils/responsive';
import { ensureAiConsent } from '../../services/ai/aiConsent';

const logger = createLogger('LetterInterview');

type Question = { key: string; question: string; placeholder: string };

type InterviewRouteProp = RouteProp<
  {
    params: {
      letterId: string;
      mode: 'draft' | 'outline';
      recipientName?: string;
      relationship?: string;
    };
  },
  'params'
>;

export const LetterInterviewScreen: React.FC = () => {
  const navigation = useNavigation<any>();
  const route = useRoute<InterviewRouteProp>();
  const { letterId, mode, recipientName, relationship } = route.params;

  const [questions, setQuestions] = useState<Question[]>([]);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [index, setIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    const load = async () => {
      // The interview's questions come from OpenAI: ask first.
      if (!(await ensureAiConsent())) {
        navigation.goBack();
        return;
      }
      try {
        const token = await getAuthToken();
        const response = await fetch(`${API_URL}/api/ai/generate/letter`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ mode: 'questions', recipientName, relationship }),
        });
        const data = await response.json();
        if (!data.success || !data.questions) {
          throw new Error(data.error || 'No questions returned');
        }
        setQuestions(data.questions);
        // Stored answers from an earlier interview seed this one.
        const stored = await letterToLovedOnesService.getInterviewAnswers();
        setAnswers(stored);
      } catch (error) {
        logger.error('Failed to load interview:', error);
        Alert.alert('Error', 'Orion could not start the interview. Please try again.');
        navigation.goBack();
      } finally {
        setLoading(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const current = questions[index];
  const isLast = index === questions.length - 1;
  const answer = current ? answers[current.key] || '' : '';

  const setAnswer = (value: string) => {
    if (!current) return;
    setAnswers((a) => ({ ...a, [current.key]: value }));
  };

  const finish = async () => {
    if (generating) return;
    try {
      setGenerating(true);
      // Persist answers first — they seed later letters to other people.
      await letterToLovedOnesService.saveInterviewAnswers(answers);

      const token = await getAuthToken();
      const response = await fetch(`${API_URL}/api/ai/generate/letter`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ mode, recipientName, relationship, answers }),
      });
      const data = await response.json();
      if (data.success && data.content) {
        navigation.replace('LetterReview', { letterId, content: data.content, mode });
      } else {
        Alert.alert('Error', data.error || 'Orion could not write right now. Please try again.');
      }
    } catch (error) {
      logger.error('Generation failed:', error);
      Alert.alert('Error', 'Orion could not write right now. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  const next = () => (isLast ? finish() : setIndex(index + 1));

  if (loading || generating) {
    return (
      <SafeAreaView style={styles.container}>
      <OrnamentBackground />
        <View style={styles.loadingWrap}>
          <ActivityIndicator color="#FFFFFF" size="large" />
          <Text style={styles.loadingTitle}>
            {generating
              ? mode === 'outline'
                ? 'Shaping your outline…'
                : 'Writing your draft…'
              : 'One moment…'}
          </Text>
          {generating && (
            <Text style={styles.loadingSub}>
              Your words, put in order. You'll be able to change every line.
            </Text>
          )}
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      <OrnamentBackground />
      <View style={styles.header}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => (index === 0 ? navigation.goBack() : setIndex(index - 1))}
          accessibilityRole="button"
          accessibilityLabel="Go back"
        >
          <Image
            source={require('../../assets/icons/back.png')}
            style={styles.backIcon}
          />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>
          Question {index + 1} of {questions.length}
        </Text>
        <View style={styles.headerSpacer} />
      </View>

      <View style={styles.sheet}>
        <ScrollView keyboardShouldPersistTaps="handled">
          <View style={styles.orionRow}>
            <Image
              source={require('../../assets/icons/chat.png')}
              style={styles.orionIcon}
            />
            <Text style={styles.orionText}>{current?.question}</Text>
          </View>

          <TextInput
            style={styles.answerInput}
            multiline
            textAlignVertical="top"
            placeholder={current?.placeholder}
            placeholderTextColor="#A0AEC0"
            value={answer}
            onChangeText={setAnswer}
            accessibilityLabel={current?.question}
          />

          <TouchableOpacity
            style={[styles.nextButton, answer.trim().length < 3 && styles.disabled]}
            onPress={next}
            disabled={answer.trim().length < 3}
            accessibilityRole="button"
            accessibilityLabel={
              isLast
                ? mode === 'outline'
                  ? 'Build my outline'
                  : 'Write my draft'
                : 'Next question'
            }
            accessibilityState={{ disabled: answer.trim().length < 3 }}
          >
            <Text style={styles.nextButtonText}>
              {isLast
                ? mode === 'outline'
                  ? 'Build my outline'
                  : 'Write my draft'
                : 'Next question'}
            </Text>
          </TouchableOpacity>

          <TouchableOpacity
            style={styles.skipButton}
            onPress={next}
            accessibilityRole="button"
            accessibilityLabel="Skip this one"
          >
            <Text style={styles.skipText}>Skip this one</Text>
          </TouchableOpacity>
        </ScrollView>
      </View>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#29BFC0',
  },
  loadingWrap: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: ms(32),
  },
  loadingTitle: {
    fontSize: ms(20, 0.3),
    fontWeight: 'bold',
    color: '#FFFFFF',
    marginTop: ms(18),
    textAlign: 'center',
  },
  loadingSub: {
    fontSize: ms(14, 0.3),
    lineHeight: ms(20, 0.3),
    color: '#FFFFFF',
    opacity: 0.9,
    marginTop: ms(8),
    textAlign: 'center',
  },
  header: {
    height: s(56),
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: ms(16),
  },
  backButton: {
    padding: ms(8),
  },
  backIcon: {
    width: s(28),
    height: s(28),
    tintColor: '#fff',
  },
  headerTitle: {
    flex: 1,
    textAlign: 'center',
    fontSize: ms(17, 0.3),
    fontWeight: '600',
    color: '#FFFFFF',
  },
  headerSpacer: {
    width: s(44),
  },
  sheet: {
    flex: 1,
    backgroundColor: '#F3F6F8',
    borderTopLeftRadius: ms(20),
    borderTopRightRadius: ms(20),
    paddingTop: ms(20),
    paddingHorizontal: ms(16),
  },
  orionRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: ms(10),
    marginBottom: ms(16),
  },
  orionIcon: {
    width: s(28),
    height: s(28),
    tintColor: '#29BFC0',
  },
  orionText: {
    flex: 1,
    fontSize: ms(16, 0.3),
    lineHeight: ms(23, 0.3),
    color: '#1C2C4F',
    fontWeight: '600',
  },
  answerInput: {
    backgroundColor: '#FFFFFF',
    borderRadius: ms(10),
    borderWidth: ms(1),
    borderColor: '#E5E5E5',
    padding: ms(15),
    minHeight: s(140),
    fontSize: ms(16, 0.3),
    lineHeight: ms(23, 0.3),
    color: '#2C4364',
    marginBottom: ms(16),
  },
  nextButton: {
    backgroundColor: '#29BFC0',
    borderRadius: ms(25),
    paddingVertical: ms(15),
    alignItems: 'center',
  },
  nextButtonText: {
    color: '#FFFFFF',
    fontSize: ms(17, 0.3),
    fontWeight: '600',
  },
  skipButton: {
    alignItems: 'center',
    paddingVertical: ms(13),
  },
  skipText: {
    fontSize: ms(15, 0.3),
    fontWeight: '600',
    color: '#6B7280',
  },
  disabled: {
    opacity: 0.5,
  },
});

export default LetterInterviewScreen;
